import styled from "@emotion/styled";
import { PropsWithChildren, useState, useEffect } from "react";
import { Typography } from "@kaltura/ds-react-components";
import Accordion, { accordionClasses } from "@mui/material/Accordion";
import AccordionSummary, { accordionSummaryClasses } from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { ChevronDown16Icon } from "@kaltura/ds-react-icons";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";

export interface FilterWrapperProps {
    title: string;
    param: string;
    disabled?: boolean;
}

const StyledFilterWrapper = styled(Accordion)(({ theme }) => ({
    backgroundColor: "transparent",
    [`&.${accordionClasses.disabled}`]: {
        backgroundColor: "transparent",
    },
    "&:before": {
        backgroundColor: theme.kaltura.palette.tone6,
        marginLeft: 0,
        marginRight: 0,
    },
    [`&.${accordionClasses.expanded}`]: {
        "&:before": {
            opacity: 1,
        },
    },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    padding: theme.spacing(0, 0),
    [`& .${accordionSummaryClasses.content}`]: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    "&:focus-visible": VisibleFocusStyle,
    [`&.${accordionSummaryClasses.focusVisible}`]: {
        backgroundColor: "transparent",
    },
}));

const StyledAccordionDetails = styled(AccordionDetails)({
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
});

const baseClassName = "kms-ds-filter";

/**
 * Wrapper for a single filter, with title and accordion
 */
export function FilterWrapper(props: PropsWithChildren<FilterWrapperProps>) {
    const sendButtonAnalytics = useButtonAnalytics();
    const { title, param, disabled, children } = props;
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded((expanded) => expanded && !disabled);
    }, [disabled]);

    const handleClick = () => {
        if (expanded) {
            sendButtonAnalytics(`Filters - Collapse filter - ${param}`, ButtonClickAnalyticsType.COLLAPSE);
        }
        else {
            sendButtonAnalytics(`Filters - Expand filter - ${param}`, ButtonClickAnalyticsType.EXPAND);
        }

        setExpanded((expanded) => !expanded);
    };

    return (
        <StyledFilterWrapper
            disableGutters
            elevation={0}
            disabled={disabled}
            expanded={expanded}
            className={`${baseClassName} ${baseClassName}-(${title})`}
        >
            <StyledAccordionSummary
                onClick={handleClick}
                expandIcon={<ChevronDown16Icon className={`${baseClassName}-expand-button`} />}
                aria-controls={`${param}-content`}
                id={`${param}-header`}
            >
                <Typography variant="body2Highlight" className={`${baseClassName}-title`}>
                    {title}
                </Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails className={`${baseClassName}-group`}>{children}</StyledAccordionDetails>
        </StyledFilterWrapper>
    );
}

export default FilterWrapper;
